import React from 'react';

import type * as api from '@gitbook/api-client';
import type * as analytics from '@gitbook/app-analytics/client';
import type { NavigationScreenSpaceState } from '@gitbook/app-navigation';
import { getAbsolutePagePathInRevision } from '@gitbook/hive-core';
import { useHiveRevision } from '@gitbook/hive-react';
import { SidePanelSheetSkeleton } from '@gitbook/react-uikit';

import {
    CommentsSidePanel,
    type CommentsSidePanelState,
    useCurrentHivePage,
} from '../../../Components';
import { useNavigation, useSetAnalyticsScreen } from '../../../navigation';

export function SpaceCommentsSidePanel(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
    screen: NavigationScreenSpaceState;
}) {
    const { space, changeRequest, screen, analyticsContext } = props;
    const { navigate } = useNavigation();
    const { revision } = useHiveRevision();
    const [currentPage] = useCurrentHivePage(revision, screen.page);

    if (screen.sideSheet?.type !== 'comments') {
        throw new Error('SpaceCommentsSidePanel should only be used with a comments side sheet');
    }

    useSetAnalyticsScreen({ ...analyticsContext, screen: 'space_discussion' }, []);

    const state: CommentsSidePanelState = {
        currentPage: currentPage?.id || null,
        sideSheet: screen.sideSheet,
    };

    const onNavigate = (newState: CommentsSidePanelState) => {
        const pagePath = newState.currentPage
            ? getAbsolutePagePathInRevision(revision, newState.currentPage)
            : undefined;
        navigate({
            current: {
                ...(typeof pagePath === 'string' ? { page: pagePath } : {}),
                sideSheet: newState.sideSheet,
            },
        });
    };

    const onClose = () => {
        navigate({
            current: {
                sideSheet: undefined,
            },
        });
    };

    return (
        <React.Suspense fallback={<SidePanelSheetSkeleton onClose={onClose} />}>
            <CommentsSidePanel
                space={space}
                changeRequest={changeRequest}
                analyticsContext={analyticsContext}
                state={state}
                onNavigate={onNavigate}
                onClose={onClose}
            />
        </React.Suspense>
    );
}
